import React from "react"
import { useLocation, useNavigate } from "react-router-dom"
import moment from "moment"
import styles from "./index.module.css"
import flight_logo from "../../assets/images/arkia.png"
import { useData } from "../../contexts/DataContext"
import Loader from "../../components/Loader"
import { addBookingToFirestore } from "../../lib/actions"

const Summary = () => {
    const [loading, setLoading] = React.useState(false)
    const navigate = useNavigate()
    const location = useLocation()
    const { airports } = useData()
    const bookingDetails = location.state?.bookingData
    const departureFlight = location.state?.departureFlight
    const returnFlight = location.state?.returnFlight
    const [isAgreed, setIsAgreed] = React.useState(false)

    const queryParams = new URLSearchParams(location.search)
    let bookingId = queryParams.get("bookingId")

    if (!bookingDetails) {
        return (
            <div className={styles.errorContainer}>
                <h3>No Booking Data Found</h3>
                <button onClick={() => navigate("/")}>Go Back to Home</button>
            </div>
        )
    }

    const handleProceedToPayment = async () => {
        setLoading(true)
        try {
            if (bookingId) {
                const queryParams = new URLSearchParams({ bookingId }).toString()
                navigate({
                    pathname: `/payment`,
                    search: `?${queryParams}`,
                })
            } else {
                bookingId = await addBookingToFirestore(bookingDetails, bookingDetails.promoCode, "pending")
                const queryParams = new URLSearchParams({ bookingId }).toString()
                navigate({
                    pathname: "/summary",
                    search: `?${queryParams}`,
                }, { state: location.state })
                navigate({
                    pathname: `/payment`,
                    search: `?${queryParams}`,
                })
                setLoading(false)
            }
        } catch (error) {
            setLoading(false)
            alert(error.message || "We're having trouble processing your booking. Please try again or contact support if the issue persists.")
        }
    }

    const handleBackToEdit = () => {
        navigate("/booking-form", {
            state: location.state,
            replace: true // Replace current entry in history stack
        })
    }

    const flights = []
    if (departureFlight) flights.push(departureFlight)
    if (returnFlight) flights.push(returnFlight)

    if (loading) return <Loader />

    return (
        <div className={styles.container}>
            <h1>Booking Summary</h1>

            <div className={styles.promoSection}>
                <div className={styles.promoCard}>
                    <div className={styles.promoHeader}>
                        <span className="material-symbols-outlined">local_offer</span>
                        <h3>PROMO CODE</h3>
                    </div>
                    <p className="text-uppercase" >{bookingDetails.promoCode}</p>
                </div>
            </div>

            <div className={styles.flightsContainer}>
                {flights.map((flight, index) => (
                    (index === 0 || (returnFlight && returnFlight.departure && returnFlight.arrival)) && (
                        <div key={index} className={styles.flightCard}>
                            <div className={styles.flightHeader}>
                                <h3>{index === 0 ? "Outbound Flight" : "Inbound Flight"}</h3>
                                <img src={flight_logo} alt="Airline Logo" />
                            </div>
                            <div className={styles.flightInfo}>
                                <div className={styles.flightDetail}>
                                    <span className="material-symbols-outlined">flight_takeoff</span>
                                    <div>
                                        <h4>{airports.find(a => a.code === flight.departure)?.label}</h4>
                                        <p>{flight.departureTime}</p>
                                    </div>
                                </div>
                                <div className={styles.flightDetail}>
                                    <span className="material-symbols-outlined">flight_land</span>
                                    <div>
                                        <h4>{airports.find(a => a.code === flight.arrival)?.label}</h4>
                                        <p>{flight.arrivalTime}</p>
                                    </div>
                                </div>
                                <div className={styles.flightMeta}>
                                    <p><span>Flight:</span> {flight.flightNo}</p>
                                    <p><span>Date:</span> {moment(flight.departureDate).format("MMM DD, YYYY")}</p>
                                    <p><span>Duration:</span> {flight.duration}</p>
                                    <p><span>Price:</span> ${flight.price}</p>
                                </div>
                            </div>
                        </div>
                    )
                ))}
            </div>

            <div className={styles.passengerSection}>
                <h2>Passenger Details</h2>
                <div className={styles.tableWrapper}>
                    <table>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Details</th>
                                <th>Passport</th>
                                <th>Preferences</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bookingDetails.passengers.map((passenger, index) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{passenger.firstName} {passenger.lastName}</td>
                                    <td>{passenger.email}</td>
                                    <td>{passenger.phone}</td>
                                    <td>
                                        <p>DOB: {moment(passenger.dob).format("MMM DD, YYYY")}</p>
                                        <p>Gender: {passenger.gender}</p>
                                        <p>Nationality: {passenger.nationality}</p>
                                    </td>
                                    <td>
                                        <p>Number: {passenger.passportNumber}</p>
                                        <p>Country: {passenger.passportCountry}</p>
                                        <p>Expires: {moment(passenger.passportExpiration).format("MMM DD, YYYY")}</p>
                                    </td>
                                    <td>
                                        <p>Seat: {passenger.seatType}</p>
                                        <p>Meal: {passenger.meal}</p>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="row">
                <div className="col-12">
                    <h6>
                        <b>Note:</b> This price takes into account the subsidy being offered by Birthright Israel for participants of these programs. IF you do not complete the full program, your credit card will later be charged the full subsidy price ($500 for one way tickets and $1,000 for round trip).
                        <br />
                        <br />
                        <input type="checkbox" checked={isAgreed} onChange={() => setIsAgreed(!isAgreed)} />&nbsp;&nbsp;
                        I acknowledge that if I do not complete the entire program, I am not eligible for the Birthright flight subsidy and my credit card will be subsequently charged up to $1,000 more.
                        {/* After payment, your information will be sent to the ticketing agency and you will receive your ticket number and record locator 48 hours before departure. By paying, you will have a confirmed seat on the flight listed above. */}
                    </h6>
                </div>
            </div>
            <div className={styles.summaryFooter}>
                <div className={styles.totalCost}>
                    <h3>Total Cost: ${bookingDetails.totalCost?.toFixed(2)}</h3>
                </div>
                <div className={styles.actions}>
                    <button className={styles.backButton} onClick={handleBackToEdit}>
                        Back to Edit
                    </button>
                    <button className={styles.confirmButton} onClick={handleProceedToPayment} disabled={!isAgreed}>
                        {loading ? 'Processing...' : 'Proceed to Payment'}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default Summary
